// The ONE source of our social marks.
//
// THE RULE (law_social_marks): every social mark is the SAME rounded-square
// outline container, with that platform's logo fitted inside it. Nothing is a
// bare glyph, nothing is a solid fill, and no logo draws its own second box.
// So `inner` here is the LOGO ONLY — the container is supplied once by whichever
// surface renders it:
//   · the site footer  → the .wl-social field (38px, 11px radius) IS the container
//   · the email footer → /api/brand/social draws the same rounded square in the PNG
// That's why instagram is a lens + dot (not a lens inside its own rect) and why
// linkedin is just the "in".
//
// Geometry: a 24×24 viewBox whose container is rect(3,3,18,18) rx 5.2 — the same
// radius ratio as the site field. Every logo is fitted to the inner ~7–17 box so
// the four read at one optical weight. Stroke-based, round caps + joins; the only
// filled parts are the instagram dot, the linkedin dot and the youtube triangle,
// drawn with `currentColor` so they take the surface ink.

export interface SocialMark {
  /** lowercase key used in /api/brand/social?name= and as the react key */
  name: string
  /** aria / alt label */
  label: string
  href: string
  /** raw inner SVG markup — the LOGO ONLY, no container box */
  inner: string
}

/** the shared container: same rounded square for every mark. */
export const SOCIAL_FRAME = { x: 3, y: 3, size: 18, rx: 5.2 }

export const SOCIAL_MARKS: SocialMark[] = [
  {
    name: 'instagram',
    label: 'Instagram',
    href: 'https://www.instagram.com/innernet.live/',
    inner:
      '<circle cx="12" cy="12" r="3.6"/>' +
      '<circle cx="16.2" cy="7.8" r="0.9" fill="currentColor" stroke="none"/>',
  },
  {
    name: 'x',
    label: 'X',
    href: 'https://x.com/yourInnernet',
    inner: '<path d="M8.6 8.6 15.4 15.4"/><path d="M15.4 8.6 8.6 15.4"/>',
  },
  {
    name: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/innernet-inc',
    inner:
      '<circle cx="8.6" cy="8.7" r="0.9" fill="currentColor" stroke="none"/>' +
      '<path d="M8.6 11.4V16.1"/>' +
      '<path d="M12.1 16.1V11.4"/>' +
      '<path d="M12.1 14.1a1.75 1.75 0 0 1 3.5 0v2"/>',
  },
  {
    name: 'youtube',
    label: 'YouTube',
    href: 'https://www.youtube.com/@Innernet',
    // YouTube's logo IS a rounded rectangle with a play triangle — so the mark
    // draws its own inner tile inside the shared field, the way the real logo
    // sits inside any app icon. Fits the field on the same optical margin the
    // instagram lens uses.
    inner:
      '<rect x="6.6" y="8.2" width="10.8" height="7.6" rx="2.2"/>' +
      '<path d="M10.9 10.05 14.2 12l-3.3 1.95z" fill="currentColor" stroke="none"/>',
  },
]

export const socialMark = (name: string): SocialMark | undefined =>
  SOCIAL_MARKS.find((m) => m.name === name.toLowerCase())
